import type { StatusTone } from '../../theme/sbgTokens';
import type { TimeOffEntry, WfhApprovalRoute, RouteDelivery } from '../../store/slices/timeOffSlice';
import { formatDateTime, formatTime } from '../../utils/format';

/**
 * Words for a WFH request's approval route (backend utils/wfhApprovalRoute.ts).
 *
 * The policy, in short: WFH is approved on arrival for someone on a hybrid
 * schedule. Anyone else's waits until they send it for VP review from the
 * emailed link (or withdraw it). Sales teams' requests go to the sales VP
 * pool, anyone else's to the VP on their record (or the default reviewer). A
 * VP can claim it (held for them for 30 minutes), defer it to another VP, or
 * decide it, by 3 PM ET the business day before the WFH day, or within two
 * business hours when it's sent after that. HR and admins can decide at any
 * point, which overrides the VP review.
 *
 * No pronouns: names repeat, because we don't know anyone's.
 */

/** Review reasons that mean the WFH policy (not an HR check) put the request on hold. */
export const WFH_POLICY_REASONS = new Set(['wfh_not_approved', 'wfh_escalated_to_vp']);

export interface RouteFlag {
  label: string;
  tone: StatusTone;
  /** One sentence for a tooltip or the review sheet. */
  detail: string;
}

/**
 * Where a pending WFH request sits, from the fields every list row already
 * has (no extra request per row). Null when the WFH policy isn't holding it.
 */
export const wfhListFlag = (entry: TimeOffEntry, now: Date = new Date()): RouteFlag | null => {
  if (entry.type !== 'WFH' || entry.status !== 'PENDING') return null;
  const name = entry.employee?.name || 'The employee';
  if (entry.reviewReason === 'wfh_not_approved') {
    return {
      label: 'Waiting on employee',
      tone: 'warning',
      detail: `Not on a hybrid schedule, so this WFH needs a VP’s approval. It reaches the VPs only when ${name} sends it for review from the emailed link.`,
    };
  }
  if (entry.reviewReason === 'wfh_escalated_to_vp' && !entry.vpDecidedAt) {
    const due = entry.vpDecisionDueAt ? new Date(entry.vpDecisionDueAt) : null;
    if (due && due.getTime() <= now.getTime()) {
      return {
        label: 'VP review overdue',
        tone: 'warning',
        detail: `A VP decision was due ${formatDateTime(due)}. Any of the reviewing VPs can take it now.`,
      };
    }
    return {
      label: 'With VPs',
      tone: 'info',
      detail: due
        ? `Sent for VP review${entry.vpEscalatedAt ? ` ${formatDateTime(entry.vpEscalatedAt)}` : ''}. A decision is due ${formatDateTime(due)}.`
        : 'Sent for VP review.',
    };
  }
  return null;
};

/** "Either of them" for two people, "Any of them" for more. */
const anyOf = (count: number) => (count === 2 ? 'Either of them' : 'Any of them');

/** "A", "A or B", "A, B or C" */
export const joinNames = (names: string[], last = 'or') => {
  if (names.length <= 1) return names[0] || '';
  return `${names.slice(0, -1).join(', ')} ${last} ${names[names.length - 1]}`;
};

export interface RouteHeadline {
  /** The pill: the state in two or three words. */
  pill: string;
  tone: StatusTone;
  /** Who has it, or what happened. */
  title: string;
  /** Why, and what happens next. */
  body: string;
}

const decidedBy = (route: WfhApprovalRoute) => {
  const decision = route.decision;
  if (!decision) return '';
  if (decision.source === 'hr_override') return decision.by ? `${decision.by.name} (HR override)` : 'HR (override)';
  return decision.by?.name || 'a VP';
};

const REMINDER_KINDS = new Set(['due_soon', 'overdue', 'overdue_escalation']);

/** A reminder reached (or at least left for) a reviewer. Being overdue alone doesn't say one was sent. */
const remindersSent = (route: WfhApprovalRoute) =>
  route.notices.some((notice) => REMINDER_KINDS.has(notice.kind)
    && notice.recipients.some((recipient) => recipient.delivery === 'sent' || recipient.delivery === 'delivered'));

/** The one-glance answer to "where is this request?". */
export const routeHeadline = (route: WfhApprovalRoute): RouteHeadline => {
  const name = route.employee.name;
  const reviewers = route.reviewers?.people.map((person) => person.name) || [];
  const reminders = remindersSent(route) ? ' Reminders have gone out.' : '';
  switch (route.stage) {
    case 'awaiting_employee':
      return {
        pill: 'Waiting on employee',
        tone: 'warning',
        title: `Waiting on ${name}`,
        body: `Not on a hybrid schedule, so this WFH needs a VP’s approval. It reaches ${reviewers.length ? joinNames(reviewers) : 'the VPs'} only when ${name} sends it for review from the emailed link, or withdraws it.`,
      };
    case 'with_vps': {
      const overdue = Boolean(route.deadline?.overdue);
      const holder = route.holder;
      const pill = overdue ? 'VP review overdue' : 'With VPs';
      const tone: StatusTone = overdue ? 'warning' : 'info';
      if (holder?.state === 'claimed' && holder.person) {
        return {
          pill,
          tone,
          title: `${holder.person.name} is reviewing it`,
          body: `Claimed${holder.at ? ` ${formatTime(holder.at)}` : ''}. A claim holds the request for that VP for 30 minutes.`,
        };
      }
      if (holder?.state === 'deferred' && holder.person) {
        const anyVp = reviewers.length === 2 ? 'either VP' : 'any of the VPs';
        const open = holder.stale
          ? ` ${holder.person.name} is no longer one of the reviewers, so ${anyVp} can take it now.`
          : holder.openToAll
            ? ` It’s overdue, so ${anyVp} can take it now.`
            : '';
        return {
          pill,
          tone,
          title: `Deferred to ${holder.person.name}`,
          body: `${holder.by ? `${holder.by.name} passed it on` : 'Passed on'}${holder.at ? ` ${formatDateTime(holder.at)}` : ''}.${open}`,
        };
      }
      return {
        pill,
        tone,
        title: reviewers.length > 1 ? `With ${joinNames(reviewers, 'and')}` : `With ${reviewers[0] || 'the VPs'}`,
        body: reviewers.length > 1
          ? `Unclaimed. ${anyOf(reviewers.length)} can claim it and decide.${reminders}`
          : `Waiting on a decision.${reminders}`,
      };
    }
    case 'with_hr':
      return {
        pill: 'With HR',
        tone: 'warning',
        title: 'Waiting on HR',
        body: 'Flagged for an HR check before it’s decided.',
      };
    case 'auto_approved': {
      // The row shows no review. Whether that was the hybrid rule is only known
      // from the employee's schedule today.
      const hybrid = route.decision?.source === 'hybrid_schedule';
      return {
        pill: 'Approved',
        tone: 'success',
        title: hybrid ? 'Approved on arrival' : 'Approved as it came in',
        body: hybrid ? `${name} is on a hybrid schedule, so WFH doesn’t need a VP.` : 'No VP review was recorded for this request.',
      };
    }
    case 'approved':
    case 'declined': {
      const approved = route.stage === 'approved';
      const verb = approved ? 'Approved' : 'Declined';
      const decision = route.decision;
      const when = decision?.at ? formatDateTime(decision.at) : null;
      if (decision?.source === 'recurring') {
        return {
          pill: verb,
          tone: 'success',
          title: 'Approved in Attendance',
          body: 'Logged as part of a recurring series, so it never went to the VPs.',
        };
      }
      if (decision?.source === 'in_app') {
        return {
          pill: verb,
          tone: approved ? 'success' : 'danger',
          title: `${verb} in Attendance`,
          body: `${when ? `Decided ${when}, ` : 'Decided '}before ${name} sent it to the VPs.`,
        };
      }
      let body = when ? `Decided ${when}.` : '';
      if (decision?.source === 'vp' && approved && decision.withLeads !== null) {
        body = `${body} ${decision.withLeads ? 'Leads approved too.' : 'Without leads.'}`.trim();
      }
      if (decision?.source === 'hr_override') body = `${body} An HR decision overrides the VP review.`.trim();
      return {
        pill: verb,
        tone: approved ? 'success' : 'danger',
        title: decision && decision.source !== 'other' ? `${verb} by ${decidedBy(route)}` : verb,
        body,
      };
    }
    case 'withdrawn':
      return {
        pill: 'Withdrawn',
        tone: 'neutral',
        title: `Withdrawn by ${name}`,
        body: 'Withdrawn from the emailed link. There’s nothing left to decide.',
      };
    case 'canceled':
    default:
      return {
        pill: 'Canceled',
        tone: 'neutral',
        title: 'Canceled in Outlook',
        body: 'The calendar event was removed, so the request was closed.',
      };
  }
};

/**
 * Who can decide, and who else may step in. Null once nobody needs to decide.
 * Before the employee sends it, the request is an ordinary pending request:
 * the manager (or a Jr manager assigned to the employee), HR or an admin can
 * decide it from the app. Once it's with the VPs, only HR and admins can,
 * which overrides the review.
 */
export const reviewersLine = (route: WfhApprovalRoute, entry?: Pick<TimeOffEntry, 'employee'>): string | null => {
  if (route.stage !== 'awaiting_employee' && route.stage !== 'with_vps') return null;
  const manager = entry?.employee?.managerName;
  const others = route.stage === 'awaiting_employee'
    ? `Until then, ${manager ? `${manager} (the manager)` : 'the manager'} or a Jr manager, HR or an admin can approve or decline it here.`
    : 'HR and admins can approve or decline at any point, which overrides the VP review.';
  if (!route.reviewers) return others;
  const names = route.reviewers.people.map((person) => person.name);
  const name = route.employee.name;
  const when = route.stage === 'awaiting_employee' ? ' once it’s sent' : '';
  if (route.reviewers.pool === 'sales') {
    return `Sales VPs${when}: ${joinNames(names, 'and')}. ${anyOf(names.length)} can claim it. ${others}`;
  }
  if (route.reviewers.pool === 'fallback') {
    return `${names[0]}${when}, the default WFH reviewer, because no active VP is set on ${name}’s record. ${others}`;
  }
  return `${names[0]}${when}, the VP on ${name}’s record. ${others}`;
};

/** When a VP decision is due, and which rule set it. */
export const deadlineLine = (route: WfhApprovalRoute): string | null => {
  const deadline = route.deadline;
  if (!deadline) return null;
  const due = formatDateTime(deadline.dueAt);
  if (deadline.overdue) return `Was due ${due}.`;
  return deadline.urgent
    ? `Due ${due}. It was sent after the 3 PM ET cutoff, so VPs get 2 business hours.`
    : `Due ${due}: 3 PM ET the business day before the WFH day.`;
};

export type RouteStepState = 'done' | 'current' | 'upcoming' | 'skipped';

export interface RouteStep {
  label: string;
  state: RouteStepState;
  detail?: string;
}

/** The route as steps, for the review sheets. Approved-on-arrival requests have none. */
export const routeSteps = (route: WfhApprovalRoute, entry: Pick<TimeOffEntry, 'createdAt' | 'isFromCalendar'>): RouteStep[] => {
  if (route.stage === 'auto_approved' || route.stage === 'with_hr' || route.stage === 'canceled' || route.decision?.source === 'recurring') return [];
  const name = route.employee.name;
  const sent = Boolean(route.escalatedAt);
  const decision = route.decision;
  const steps: RouteStep[] = [
    {
      label: 'Submitted',
      state: 'done',
      detail: [entry.createdAt ? formatDateTime(entry.createdAt) : null, entry.isFromCalendar ? 'From Outlook' : 'In Attendance'].filter(Boolean).join(' · '),
    },
    { label: 'Needs a VP', state: 'done', detail: 'Not on a hybrid schedule' },
  ];

  if (sent) {
    steps.push({ label: 'Sent for VP review', state: 'done', detail: `By ${name}, ${formatDateTime(route.escalatedAt as string)}` });
  } else if (route.stage === 'awaiting_employee') {
    steps.push({ label: 'Sent for VP review', state: 'current', detail: `Waiting on ${name}` });
  } else {
    steps.push({ label: 'Sent for VP review', state: 'skipped', detail: 'Not sent' });
  }

  if (route.stage === 'with_vps') {
    const headline = routeHeadline(route);
    steps.push({
      label: 'VP decision',
      state: 'current',
      detail: [headline.title, route.deadline ? `due ${formatDateTime(route.deadline.dueAt)}` : null].filter(Boolean).join(' · '),
    });
  } else if (decision?.source === 'vp') {
    steps.push({ label: 'VP decision', state: 'done', detail: [decision.by?.name, decision.at ? formatDateTime(decision.at) : null].filter(Boolean).join(', ') });
  } else if (route.stage === 'awaiting_employee') {
    steps.push({ label: 'VP decision', state: 'upcoming' });
  } else {
    steps.push({
      label: 'VP decision',
      state: 'skipped',
      detail: decision?.source === 'hr_override' ? 'Overridden by HR' : decision?.source === 'in_app' ? 'Decided in Attendance first' : 'Not needed',
    });
  }

  if (route.stage === 'approved' || route.stage === 'declined') {
    steps.push({ label: route.stage === 'approved' ? 'Approved' : 'Declined', state: 'done', detail: routeHeadline(route).title });
  } else if (route.stage === 'withdrawn') {
    steps.push({ label: 'Withdrawn', state: 'done', detail: `By ${name}` });
  } else {
    steps.push({ label: 'Approved or declined', state: 'upcoming' });
  }
  return steps;
};

const NOTICE_LABEL: Record<WfhApprovalRoute['notices'][number]['kind'], string> = {
  employee_request: 'Asked to send it for VP review',
  initial: 'VP review request',
  daily_digest: 'Daily digest',
  due_soon: 'Due-soon reminder',
  overdue: 'Overdue reminder',
  overdue_escalation: 'Overdue escalation',
  overdue_escalation_copy: 'Overdue copy, outside the reviewers',
  vp_defer: 'Deferred to another VP',
  vp_decision_notice: 'Decision notice to the other VPs',
  hr_closure_notice: 'HR closed the VP review',
  employee_result: 'Decision sent to the employee',
  rescind_notice: 'Withdrawal notice to the VPs',
};

export const noticeLabel = (kind: WfhApprovalRoute['notices'][number]['kind']) => NOTICE_LABEL[kind] ?? kind;

/** A send's delivery in a word or two, only when it's worth saying: failures and sends still in flight. */
export const deliveryNote = (recipients: Array<{ name: string; delivery: RouteDelivery }>): { tone: StatusTone; text: string } | null => {
  const failed = recipients.filter((recipient) => recipient.delivery === 'failed');
  if (failed.length) {
    return {
      tone: 'danger',
      text: failed.length === recipients.length ? 'Not delivered' : `Not delivered to ${joinNames(failed.map((recipient) => recipient.name), 'and')}`,
    };
  }
  if (recipients.some((recipient) => recipient.delivery === 'sending')) return { tone: 'neutral', text: 'Sending' };
  if (recipients.some((recipient) => recipient.delivery === 'unknown')) return { tone: 'neutral', text: 'Delivery unconfirmed' };
  if (recipients.length && recipients.every((recipient) => recipient.delivery === 'delivered')) return { tone: 'success', text: 'Delivered' };
  return null;
};
